import { useMemo } from "react";
import { useSnapshot } from "valtio";
import { analyticsStore } from "../stores/analytics";
import {
  useAnalyticsSessions,
  computeOverviewFromSessions,
} from "../api/analytics-api";
import MetricCard from "@/components/MetricCard";
import { LineChart } from "../components/charts";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Users, Activity, Clock, AlertCircle } from "lucide-react";
import { formatNumber, formatDurationHuman } from "@/lib/utils";
import type { AnalyticsSession } from "@/lib/instantdb";

export default function AnalyticsOverview() {
  const { timeRange } = useSnapshot(analyticsStore);
  const { data, isLoading, error } = useAnalyticsSessions(timeRange);

  const sessions = (data?.analytics_sessions ?? []) as AnalyticsSession[];

  // Compute overview metrics from InstantDB data
  const overview = useMemo(() => {
    if (sessions.length === 0) return null;
    return computeOverviewFromSessions(sessions, timeRange);
  }, [sessions, timeRange]);

  // Generate DAU data for chart from sessions
  const dauData = useMemo(() => {
    const dayMs = 24 * 60 * 60 * 1000;
    const now = Date.now();
    const daysToShow = Math.min(timeRange, 30);

    // Create buckets for each day
    const buckets: Record<string, Set<string>> = {};
    for (let i = 0; i < daysToShow; i++) {
      const date = new Date(now - i * dayMs).toISOString().split("T")[0];
      buckets[date] = new Set();
    }

    // Fill buckets with unique users per day
    for (const session of sessions) {
      const date = new Date(session.startedAt).toISOString().split("T")[0];
      if (buckets[date]) {
        buckets[date].add(session.userId);
      }
    }

    // Convert to chart data
    return Object.entries(buckets)
      .map(([date, users]) => ({
        name: new Date(date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        value: users.size,
      }))
      .reverse();
  }, [sessions, timeRange]);

  const loading = isLoading;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      {/* Error State */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error instanceof Error
              ? error.message
              : typeof error === "object"
                ? JSON.stringify(error)
                : String(error)}
          </AlertDescription>
        </Alert>
      )}

      {/* Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
        {loading ? (
          <>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </>
        ) : (
          <>
            <MetricCard
              title="Daily Active Users"
              value={formatNumber(overview?.dau ?? 0)}
              subtitle={`Last ${timeRange} days`}
              icon={<Users className="h-4 w-4" />}
            />
            <MetricCard
              title="Monthly Active Users"
              value={formatNumber(overview?.mau ?? 0)}
              subtitle="This month"
              icon={<Users className="h-4 w-4" />}
            />
            <MetricCard
              title="Total Sessions"
              value={formatNumber(overview?.total_sessions ?? 0)}
              subtitle={`Last ${timeRange} days`}
              icon={<Activity className="h-4 w-4" />}
            />
            <MetricCard
              title="Avg Session Duration"
              value={formatDurationHuman(
                overview?.avg_session_duration_seconds ?? 0,
              )}
              subtitle="Per session"
              icon={<Clock className="h-4 w-4" />}
            />
          </>
        )}
      </div>

      {/* Chart */}
      {loading ? (
        <Skeleton className="h-[400px]" />
      ) : (
        <LineChart
          data={dauData}
          title="Daily Active Users"
          dataKey="value"
          color="#667eea"
          height={350}
        />
      )}
    </div>
  );
}
